/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { environment } from '@env/environment';

/**
 * Includes the restrictions that the accounts have for specific project environments.
 *
 * @exports Functions that are used for checking the restrictions in the app.
 */
export namespace OSKAccountRestrictions {
  /**
   * Project ids that are retricted to accept only specific email domains
   */
  const restrictedProjectIds = ['dev-oskey-io', 'staging-oskey-io'] as const;

  /**
   * Allowed email domains for each restricted project
   */
  const allowedEmailDomainsByProjectId: Record<(typeof restrictedProjectIds)[number], string[]> = {
    'dev-oskey-io': ['@oskey.io'],
    'staging-oskey-io': ['@oskey.io']
  };

  /**
   * Checks if the given email domain is allowed in the current project environment.
   *
   * @param { string | undefined } email The email that needs to be checked
   * @returns { boolean } True/False
   */
  export const isAccountEmailAllowed = (email?: string): boolean => {
    if (!email) {
      return false;
    }

    if (
      restrictedProjectIds.includes(
        environment.firebaseConfig.projectId as (typeof restrictedProjectIds)[number]
      ) &&
      !allowedEmailDomainsByProjectId[
        environment.firebaseConfig.projectId as (typeof restrictedProjectIds)[number]
      ].some((domain) => email.endsWith(domain))
    ) {
      return false;
    }

    return true;
  };
}
